import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMeetingApiUrl, BASE_URLS } from '../config/globalConfig';
import './LiveClass.css';

// Global flag to prevent multiple tab openings across all instances
let globalTabOpened = false;

const LiveClass = () => {
    const { classId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [classData, setClassData] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [meetingStatus, setMeetingStatus] = useState({
        teacher_joined: false,
        student_joined: false,
        is_started: false
    });
    const [userRole, setUserRole] = useState('');
    const [inWaitingRoom, setInWaitingRoom] = useState(false);
    const [waitingForZoomRedirect, setWaitingForZoomRedirect] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(5);
    const [showManualJoinButton, setShowManualJoinButton] = useState(false);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingFeedback, setRatingFeedback] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [ratingPhaseAfterCountdown, setRatingPhaseAfterCountdown] = useState(false);
    const [meetingTabOpened, setMeetingTabOpened] = useState(false);
    const statusCheckIntervalRef = useRef(null);
    const countdownTriggeredRef = useRef(false);
    const tabOpenAttemptedRef = useRef(false);

    const getUserData = () => {
        const getCookie = (name) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        };
        const userId = getCookie('User_Id');
        const userRole = getCookie('User_Role');
        const userFirstName = getCookie('User_FirstName');
        const userLastName = getCookie('User_LastName');
        if (!userId || !userRole) return null;
        return {
            id: parseInt(userId, 10),
            role: userRole,
            name: `${userFirstName || ''} ${userLastName || ''}`.trim() || 'Participant'
        };
    };

    const api = {
        checkAccess: async () => {
            const userData = getUserData();
            const res = await fetch(getMeetingApiUrl('/check-access'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    class_id: parseInt(classId, 10),
                    user_id: userData.id,
                    user_role: userData.role
                })
            });
            return res.json();
        },
        verifyPassword: async (pwd) => {
            const res = await fetch(getMeetingApiUrl('/verify-password'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ class_id: parseInt(classId, 10), password: pwd })
            });
            return res.json();
        },
        updateAttendance: async (action) => {
            const userData = getUserData();
            const res = await fetch(getMeetingApiUrl('/attendance'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    class_id: parseInt(classId, 10),
                    user_id: userData.id,
                    user_role: userData.role,
                    action
                })
            });
            return res.json();
        },
        getStatus: async () => {
            const res = await fetch(getMeetingApiUrl(`/status?class_id=${classId}&t=${Date.now()}`));
            return res.json();
        },
        startClass: async () => {
            const userData = getUserData();
            const res = await fetch(getMeetingApiUrl('/start-class'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    class_id: parseInt(classId, 10),
                    teacher_id: userData.id
                })
            });
            return res.json();
        },
        submitClassRating: async (ratingValue, feedback) => {
            const userData = getUserData();
            const res = await fetch(getMeetingApiUrl('/class-rating'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    class_id: parseInt(classId, 10),
                    user_id: userData.id,
                    user_role: userData.role,
                    rating: ratingValue,
                    feedback: feedback || ''
                })
            });
            return res.json();
        },
    };

    const handleJoinClass = async (classInfo, role) => {
        try {
            // First update attendance and meeting status
            await api.updateAttendance('join');
            if (role.toLowerCase() === 'teacher') {
                await api.startClass();
            }

            // Update meeting status in state
            setMeetingStatus(prev => ({
                ...prev,
                teacher_joined: role.toLowerCase() === 'teacher' ? true : prev.teacher_joined,
                student_joined: role.toLowerCase() === 'student' ? true : prev.student_joined,
                is_started: role.toLowerCase() === 'teacher' ? true : prev.is_started
            }));

            // Check if live_meeting_url exists and handle based on user role
            if (classInfo?.live_meeting_url && classInfo.live_meeting_url.trim() !== '') {
                const isTeacher = role.toLowerCase() === 'teacher';
                const isAdmin = role.toLowerCase() === 'admin';
                const isManager = role.toLowerCase() === 'manager';
                
                if (isTeacher || isAdmin || isManager) {
                    // Teacher, Admin, Manager: show manual join button
                    setShowManualJoinButton(true);
                    return;
                } else {
                    // Waiting room disabled: student can always join immediately
                    setShowManualJoinButton(true);
                    return;
                }
            }

            // Set user state based on role (only if no Zoom URL)
            if (role.toLowerCase() === 'teacher') {
            } else {
                // Waiting room disabled: don't block student on teacher_joined status
                const statusRes = await api.getStatus();
                if (statusRes.success && statusRes.teacher_joined) {
                    setMeetingStatus(prev => ({ ...prev, teacher_joined: true }));
                }
            }
        } catch (error) {
            console.error('Error during class join:', error);
            setClassData({ error: 'Failed to join class. Please try again.' });
        }
    };

    const checkClassAccess = async () => {
        try {
            const userData = getUserData();
            if (!classId || classId === 'undefined' || classId === 'null') {
                setClassData({ error: 'Invalid class ID' });
                setLoading(false);
                return;
            }
            if (!userData) {
                navigate('/');
                return;
            }
            if (!BASE_URLS.HSM_SERVER_URL) {
                setClassData({ error: 'Meeting server not configured. Set HSM_SERVER_URL in config.' });
                setLoading(false);
                return;
            }

            const result = await api.checkAccess();

            if (result.success) {
                setClassData(result.class);
                setUserRole(result.user_role);

                const role = String(result.user_role).toLowerCase();
                
                const teacherAttend = Number(result.class.teacher_attend);
                const isStarted = Number(result.class.is_started);
                setMeetingStatus({
                    teacher_joined: teacherAttend === 1,
                    student_joined: Number(result.class.student_attend) === 1,
                    is_started: isStarted === 1
                });

                // Skip password modal - directly handle join (removes enter password screen)
                await handleJoinClass(result.class, role);
            } else {
                if (result.message && result.message.includes('completed')) {
                    setClassData({ is_completed: true });
                } else {
                    setClassData({ not_authorized: true });
                }
            }
        } catch (error) {
            console.error('Error checking class access:', error);
            setClassData({ error: 'Failed to check class access' });
        } finally {
            setLoading(false);
        }
    };

    const verifyPassword = async () => {
        const requiresPassword = classData?.class_join_password && String(classData.class_join_password).trim() !== '';
        if (requiresPassword && !password.trim()) {
            setPasswordError('Password is required');
            return;
        }

        try {
            const verifyRes = await api.verifyPassword(password);
            if (!verifyRes.success && requiresPassword) {
                setPasswordError(verifyRes.message || verifyRes.error || 'Incorrect password');
                return;
            }

            setShowPasswordModal(false);
            setPasswordError('');

            // Use the common join handler
            await handleJoinClass(classData, userRole);
        } catch (error) {
            console.error('Error verifying password:', error);
            setPasswordError('Failed to verify password');
        }
    };

    const startRedirectCountdown = () => {
        console.log('startRedirectCountdown called, countdownTriggeredRef:', countdownTriggeredRef.current);
        
        // Prevent multiple countdown executions
        if (countdownTriggeredRef.current) {
            console.log('Countdown already triggered, skipping...');
            return;
        }
        countdownTriggeredRef.current = true;
        
        setRedirectCountdown(5);
        const countdownInterval = setInterval(() => {
            setRedirectCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownInterval);
                    console.log('Countdown finished, opening meeting tab. meetingTabOpened:', meetingTabOpened, 'tabOpenAttemptedRef:', tabOpenAttemptedRef.current);
                    
                    // Ultimate protection: check global flag, state, and ref before opening
                    if (!globalTabOpened && !meetingTabOpened && !tabOpenAttemptedRef.current) {
                        console.log('Opening meeting URL in new tab');
                        globalTabOpened = true;
                        tabOpenAttemptedRef.current = true;
                        const zoomUrl = new URL(classData.live_meeting_url);
                        zoomUrl.searchParams.set('from_hsm', 'true');
                        window.open(zoomUrl.toString(), '_blank');
                        setMeetingTabOpened(true);
                    } else {
                        console.log('Tab already opened or attempted, skipping... globalTabOpened:', globalTabOpened);
                    }
                    
                    const role = userRole.toLowerCase();
                    
                    if (role === 'admin' || role === 'manager') {
                        // Admin and Manager: redirect directly to dashboard, no rating modal
                        window.location.href = BASE_URLS.ADMIN_DASHBOARD_URL;
                    } else {
                        // Teacher and Student: redirect to class review/rating page
                        const userData = getUserData();
                        const userId = userData?.id;
                        const classReviewUrl = `${BASE_URLS.ADMIN_PANEL_BASE_URL}/dashboard/edit_class_review_rating.php?cid=${classId}&uid=${userId}`;
                        window.location.href = classReviewUrl;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSubmitRatingAndRedirect = async () => {
        const numericRating = parseInt(ratingValue, 10);
        const isTeacher = userRole?.toLowerCase() === 'teacher';
        
        if (!numericRating || numericRating < 1 || numericRating > 5) {
            setRatingError('Please select a rating from 1 to 5, or use "Skip" to continue without rating.');
            return;
        }
        
        if (isTeacher) {
            if (!ratingFeedback.trim()) {
                setRatingError('Review is required for any selected rating to send class update and email to student.');
                return;
            }
        } else if (numericRating <= 3 && !ratingFeedback.trim()) {
            setRatingError('Please enter a short review when giving 3 stars or less.');
            return;
        }
        
        setRatingError('');
        setRatingSubmitting(true);
        
        try {
            const res = await api.submitClassRating(numericRating, ratingFeedback);
            if (!res?.success) {
                setRatingError(res?.error || res?.message || 'Failed to save rating. Please try again.');
                setRatingSubmitting(false);
                return;
            }
            
            // Close modal and redirect to admin dashboard in same tab
            setShowRatingModal(false);
            setRatingSubmitting(false);
            window.location.href = BASE_URLS.ADMIN_DASHBOARD_URL;
        } catch (e) {
            console.error('submitClassRating error:', e);
            setRatingError('Failed to submit rating. Please try again.');
            setRatingSubmitting(false);
        }
    };

    const handleSkipRatingAndRedirect = () => {
        setRatingError('');
        setShowRatingModal(false);
        // Close modal and redirect to admin dashboard in same tab
        window.location.href = BASE_URLS.ADMIN_DASHBOARD_URL;
    };

    const handleManualJoinMeeting = () => {
        // Open meeting link in new tab
        if (classData?.live_meeting_url && classData.live_meeting_url.trim() !== '') {
            const zoomUrl = new URL(classData.live_meeting_url);
            zoomUrl.searchParams.set('from_hsm', 'true');
            window.open(zoomUrl.toString(), '_blank');
            setMeetingTabOpened(true);
        }

        // Show rating modal in current tab
        const role = userRole.toLowerCase();
        
        if (role === 'admin' || role === 'manager') {
            // Admin and Manager: redirect directly to dashboard, no rating modal
            window.location.href = BASE_URLS.ADMIN_DASHBOARD_URL;
        } else {
            // Teacher and Student: show rating modal
            // setRatingPhaseAfterCountdown(true);
            // setRatingError('');
            // setRatingSubmitting(false);
            // setRatingValue(0);
            // setRatingFeedback('');
            // setShowRatingModal(true);

            const userData = getUserData();
            const userId = userData?.id;
            
            const classReviewUrl = `${BASE_URLS.ADMIN_PANEL_BASE_URL}/dashboard/edit_class_review_rating.php?cid=${classId}&uid=${userId}`;
            window.location.href = classReviewUrl;
        }
    };

    const checkMeetingStatus = async () => {
        try {
            const result = await api.getStatus();
            if (result.success) {
                setMeetingStatus({
                    teacher_joined: result.teacher_joined,
                    student_joined: result.student_joined,
                    is_started: result.is_started
                });
                
                // If teacher has joined and started class, show join button to student
                if (result.teacher_joined && result.is_started && inWaitingRoom && 
                    classData?.live_meeting_url && classData.live_meeting_url.trim() !== '') {
                    
                    console.log('Teacher joined and started class, showing join button...');
                    setInWaitingRoom(false);
                    setShowManualJoinButton(true);
                    
                    // Clear the interval since we're showing join button
                    if (statusCheckIntervalRef.current) {
                        clearInterval(statusCheckIntervalRef.current);
                        statusCheckIntervalRef.current = null;
                    }
                }
            }
        } catch (error) {
            console.error('Error checking meeting status:', error);
        }
    };

    useEffect(() => {
        checkClassAccess();
    }, []);

    useEffect(() => {
        if (inWaitingRoom) {
            // Check status every second
            statusCheckIntervalRef.current = setInterval(checkMeetingStatus, 1000);
        }
        return () => {
            if (statusCheckIntervalRef.current) {
                clearInterval(statusCheckIntervalRef.current);
                statusCheckIntervalRef.current = null;
            }
        };
    }, [inWaitingRoom]);

    const renderClassRatingModal = () => (
        <div className="whiteboard-modal-overlay">
            <div className="whiteboard-modal" onClick={(e) => e.stopPropagation()}>
                <div className="whiteboard-header">
                    <h5>
                        {userRole?.toLowerCase() === 'teacher' ? 'Rating Required' : 'Rate this class'}
                    </h5>
                    {userRole?.toLowerCase() !== 'teacher' ? (
                        <button
                            type="button"
                            className="whiteboard-close-btn"
                            onClick={() => {
                                if (ratingSubmitting) return;
                                handleSkipRatingAndRedirect();
                            }}
                            aria-label="Close"
                            disabled={ratingSubmitting}
                        >
                            ×
                        </button>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="whiteboard-content">
                    <div className="rating-row">
                        <label>Rating</label>
                        <div className="rating-stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`rating-star-btn ${Number(ratingValue) >= star ? 'selected' : ''}`}
                                    onClick={() => setRatingValue(star)}
                                    disabled={ratingSubmitting}
                                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                                >
                                    <span className="rating-star-symbol">★</span>
                                </button>
                            ))}
                        </div>
                        <div className="rating-selected-text">
                            {ratingValue
                                ? `${ratingValue} star${ratingValue > 1 ? 's' : ''} selected`
                                : 'Select a rating'}
                        </div>
                    </div>
                    <div className="rating-row">
                        <label htmlFor="class-rating-feedback-modal">
                            Review{' '}
                            {userRole?.toLowerCase() === 'teacher'
                                ? '(required for any rating – sent as class update & email to student)'
                                : Number(ratingValue) <= 3
                                  ? '(required for 1–3)'
                                  : '(optional for 4–5)'}
                        </label>
                        <textarea
                            id="class-rating-feedback-modal"
                            className="whiteboard-textarea"
                            rows={4}
                            value={ratingFeedback}
                            onChange={(e) => setRatingFeedback(e.target.value)}
                            placeholder="Share your feedback about this class…"
                            disabled={ratingSubmitting}
                        />
                    </div>
                    {ratingError && <div className="whiteboard-error">{ratingError}</div>}
                </div>
                <div className="whiteboard-footer">
                    <button
                        type="button"
                        className="whiteboard-save-btn"
                        disabled={ratingSubmitting}
                        onClick={handleSubmitRatingAndRedirect}
                    >
                        {ratingSubmitting ? 'Submitting…' : 'Submit Rating'}
                    </button>
                    {userRole?.toLowerCase() !== 'teacher' && (
                        <button
                            type="button"
                            className="whiteboard-cancel-btn"
                            disabled={ratingSubmitting}
                            onClick={handleSkipRatingAndRedirect}
                        >
                            Skip Rating
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // Waiting room disabled: students are never held back with a "Class Not Started" screen
    const studentWaitingNotJoined = false;

    if (loading) {
        return (
            <div className="live-class-container">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    if (classData?.is_completed) {
        return (
            <div className="live-class-container">
                <div className="live-class-error-message">
                    <h2>Class Completed</h2>
                    <p>This class has already been completed. Please join another class.</p>
                    <button onClick={() => navigate('/')} className="btn-primary error-btn-primary">Go Home</button>
                </div>
            </div>
        );
    }

    if (classData?.not_authorized) {
        return (
            <div className="live-class-container">
                <div className="live-class-error-message">
                    <h2>Access Denied</h2>
                    <p>You are not authorized to join this class.</p>
                    <button onClick={() => window.location.href = BASE_URLS.ADMIN_DASHBOARD_URL} className="btn-primary error-btn-primary">Go to Dashboard</button>
                </div>
            </div>
        );
    }

    if (classData?.error) {
        return (
            <div className="live-class-container">
                <div className="live-class-error-message">
                    <h2>Error</h2>
                    <p>{classData.error}</p>
                    <button onClick={() => navigate('/')} className="btn-primary error-btn-primary">Go Home</button>
                </div>
            </div>
        );
    }

    if (showManualJoinButton) {
        return (
            <div className="live-class-container">
                <div className="password-modal">
                    <div className="modal-content">
                        <h2>Join Meeting</h2>
                        <p>Click the button below to join the class meeting in a new tab.</p>
                        <div className="modal-actions">
                            <button onClick={handleManualJoinMeeting} className="btn-meeting-join btn-primary">
                                Join Meeting
                            </button>
                        </div>
                    </div>
                </div>
                {showRatingModal && renderClassRatingModal()}
            </div>
        );
    }

    if (waitingForZoomRedirect || ratingPhaseAfterCountdown) {
        return (
            <div className="live-class-container">
                <div className="password-modal">
                    <div className="modal-content">
                        {!ratingPhaseAfterCountdown && (
                            <>
                                <div className="loading-spinner"></div>
                                <h2>Redirecting to Meeting</h2>
                                <p>Please wait, you'll be redirected to your class meeting.</p>
                                <div className="redirect-timer">
                                    <span>Redirecting in {redirectCountdown} {redirectCountdown === 1 ? 'second' : 'seconds'}...</span>
                                </div>
                            </>
                        )}
                        {ratingPhaseAfterCountdown && (
                            <>
                                <h2>Rate This Class</h2>
                                <p>Please rate this class before joining the meeting, or skip to continue.</p>
                            </>
                        )}
                    </div>
                </div>
                {showRatingModal && renderClassRatingModal()}
            </div>
        );
    }

    if (studentWaitingNotJoined) {
        return (
            <div className="live-class-container">
                <div className="password-modal">
                    <div className="modal-content">
                        <h2>Class Not Started</h2>
                        <p>The teacher has not started the class yet. Please wait for the teacher to join.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (showPasswordModal) {
        const requiresPassword = classData?.class_join_password && String(classData.class_join_password).trim() !== '';
        return (
            <div className="live-class-container">
                <div className="password-modal">
                    <div className="modal-content">
                        <h2>{requiresPassword ? 'Enter Class Password' : 'Join Class'}</h2>
                        {requiresPassword ? (
                            <div className="form-group">
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter class password"
                                    className="password-input"
                                />
                                {passwordError && <div className="error">{passwordError}</div>}
                            </div>
                        ) : (
                            <p>Click &quot;Join Class&quot; to enter the live class.</p>
                        )}
                        <div className="modal-actions">
                            <button onClick={() => navigate('/')} className="btn-secondary">Cancel</button>
                            <button onClick={verifyPassword} className="btn-primary">
                                {requiresPassword ? 'Submit' : 'Join Class'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    
    if (inWaitingRoom) {
        return (
            <div className="live-class-container">
                <div className="password-modal">
                    <div className="modal-content waiting-room-content">
                        <h2>Waiting Room</h2>
                        <p>You have joined the class. Please wait for the teacher to join the meeting.</p>
                        <div className="waiting-room-spinner" />
                    </div>
                </div>
                {showRatingModal && renderClassRatingModal()}
            </div>
        );
    }

    // Add rating modal to all other states
    return (
        <div className="live-class-container">
            {showRatingModal && renderClassRatingModal()}
        </div>
    );
};

export default LiveClass;
